type ContractedTag = { id: string; title?: string };

export type ClientTypes = {
  /** Tem contrato de assinatura (`forum-signature`). Confiável. */
  has_assinatura: boolean;
  /** Comprou livro avulso na loja (`book_`), acesso perpétuo. Confiável. */
  has_loja: boolean;
  /** Tem série de livro/vídeo ou revista contratada, de qualquer origem. Confiável. */
  has_sold_content: boolean;
  /**
   * Cliente de venda (série comprada fora de assinatura).
   *
   * ⚠️ Best-effort: as tags chegam ACHATADAS (união de todos os contratos), então
   * não dá para saber de qual contrato veio cada série. Um cliente que tem um
   * contrato de assinatura E um contrato de venda separado sai daqui como só
   * assinatura (falso-negativo). Para distinguir de verdade é preciso classificar
   * POR CONTRATO, como faz `classify_contracts` no admin-plat-client-config.
   *
   * Para saber se o cliente depende de contrato vigente, use `has_sold_content ||
   * has_assinatura` em vez deste campo.
   */
  has_venda: boolean;
};

/**
 * Deriva o tipo do cliente a partir das tags contratadas, substituindo o antigo
 * `client.profile` — que era preenchido manualmente e ficava errado quando o
 * cadastro era feito pelo menu errado do admin.
 *
 * Regra (mesma do backend): `forum-signature` + tags de série = assinatura;
 * só tags de série = venda. Um contrato de assinatura sempre carrega séries e
 * revistas junto, por isso a presença de `forum-signature` tem precedência.
 */
export function derive_client_types(tags: ContractedTag[] = []): ClientTypes {
  const ids = (tags || [])
    .map((tag) => (tag ? tag.id : ''))
    .filter((id): id is string => !!id);

  const has_assinatura = ids.some((id) => id.includes('forum-signature'));

  const has_sold_content = ids.some(
    (id) =>
      id.includes('book-serie') ||
      id.includes('video-serie') ||
      id.includes('periodic')
  );

  return {
    has_assinatura,
    has_sold_content,
    has_loja: ids.some((id) => id.startsWith('book_')),
    has_venda: has_sold_content && !has_assinatura
  };
}

/**
 * Cliente cujo acesso a NOVIDADES depende de um contrato vigente — venda ou
 * assinatura. Cliente só de loja (livro avulso) tem acesso perpétuo e fica fora.
 */
export function depends_on_active_contract(tags: ContractedTag[] = []): boolean {
  const { has_sold_content, has_assinatura } = derive_client_types(tags);

  return has_sold_content || has_assinatura;
}
